import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ChatCardPayload } from '../../types/chat';
import { usePermissions } from '../../hooks/usePermissions';
import { PERM } from '../../auth/permissions';
import { useImageUrl } from '../../services/uploads';
import { getCourses } from '../../api/me';

interface CardMessageProps {
  payload: ChatCardPayload;
}

/**
 * CardMessage — rich invitation card for `kind: 'card'` chat messages.
 *
 * The same stored card renders a **role-aware** CTA per viewer: a manager
 * (course/program permission) is sent to the configure page; a learner is sent
 * to start the course/program. The learner course-start needs the per-viewer
 * assignment id (the player route is keyed by assignment, not course), so we
 * resolve it from `/me/courses` — fetched only for that case.
 */
const CardMessage: React.FC<CardMessageProps> = ({ payload }) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [imageUrl] = useImageUrl(payload.image_url ?? null);

  const isProgram = payload.target_type === 'program';
  const canManage = isProgram
    ? can(PERM.PROGRAM_MANAGE)
    : can(PERM.COURSE_AUTHOR) || can(PERM.COURSE_ASSIGN);

  const needsAssignment = !canManage && payload.target_type === 'course';
  const { data: buckets } = useQuery({
    queryKey: ['me', 'courses'],
    queryFn: getCourses,
    enabled: needsAssignment,
    staleTime: 5 * 60 * 1000,
  });
  // The course→assignment lookup must resolve before the Start CTA is safe to
  // click (otherwise it would briefly point at the bare /learn/courses list).
  const ctaReady = !needsAssignment || buckets !== undefined;

  const { label, to } = (() => {
    if (canManage) {
      return isProgram
        ? { label: 'Open program settings', to: `/programs/${payload.target_id}/configure` }
        : { label: 'Open course settings', to: `/courses/${payload.target_id}/configure` };
    }
    if (isProgram) {
      return { label: 'Start program', to: `/learn/programs/${payload.target_id}` };
    }
    // Learner course → resolve course_id to this viewer's assignment id.
    const all = buckets
      ? [...buckets.in_progress, ...buckets.upcoming, ...buckets.completed]
      : [];
    const match = all.find((c) => c.course_id === payload.target_id);
    return {
      label: 'Start course',
      to: match?.assignment_id ? `/learn/courses/${match.assignment_id}` : '/learn/courses',
    };
  })();

  const eyebrow = isProgram ? 'Program invitation' : 'Course invitation';

  return (
    <div className="max-w-[420px] w-full overflow-hidden rounded-2xl border border-border-primary/15 bg-surface-elevated shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
      {imageUrl && (
        <div className="h-32 w-full overflow-hidden bg-neutral-100 dark:bg-neutral-800">
          <img src={imageUrl} alt="" className="h-full w-full object-cover" />
        </div>
      )}
      <div className="flex flex-col gap-2 px-4 py-4">
        <span className="text-[11px] font-bold uppercase tracking-wide text-primary-500">
          {eyebrow}
        </span>
        <h4 className="text-[15px] font-bold leading-snug text-text-primary">{payload.title}</h4>
        {payload.description && (
          <p className="text-[13px] leading-relaxed text-text-secondary line-clamp-2">
            {payload.description}
          </p>
        )}
        <button
          type="button"
          onClick={() => navigate(to)}
          disabled={!ctaReady}
          className="mt-2 inline-flex items-center justify-center gap-1.5 self-start rounded-full bg-primary-500 px-4 py-1.5 text-sm font-bold transition-[background-color,transform] duration-150 ease-out hover:bg-primary-600 dark:hover:bg-primary-400 active:scale-[0.97] disabled:opacity-60 disabled:cursor-wait disabled:active:scale-100"
        >
          {ctaReady ? label : 'Loading…'}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M5 12h14M12 5l7 7-7 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CardMessage;
